import { useMemo, useState } from "react";
import { Download, Search, Trash2 } from "lucide-react";
import { toast } from "sonner";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { api, errorMessage, INTERESTS } from "@/lib/api";
import { NoteCell, InviteBar } from "./RowTools";

export const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });

const exportCsv = (rows, cols, name) => {
  const esc = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;
  const all = [...cols, ["contacted", "Contacted"], ["note", "Note"], ["invited_at", "Invited at"]];
  const csv = [all.map((c) => esc(c[1])).join(","), ...rows.map((r) => all.map(([k]) => esc(r[k])).join(","))].join("\n");
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([csv], { type: "text/csv" }));
  a.download = `${name}-${new Date().toISOString().slice(0, 10)}.csv`;
  a.click();
};

const Filters = ({ name, q, setQ, status, setStatus, interest, setInterest, showInterest }) => (
  <div className="flex flex-wrap gap-3 flex-1">
    <div className="relative flex-1 min-w-[200px] max-w-sm">
      <Search size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--tn-muted)]" />
      <input className="tn-input h-9 pl-9" placeholder="Search…" value={q} onChange={(e) => setQ(e.target.value)} data-testid={`admin-${name}-search`} />
    </div>
    <select className="tn-input h-9 w-auto" value={status} onChange={(e) => setStatus(e.target.value)} data-testid={`admin-${name}-status-filter`}>
      <option value="all">All statuses</option>
      <option value="new">Not contacted</option>
      <option value="contacted">Contacted</option>
      {showInterest && <option value="invited">Invited</option>}
      {showInterest && <option value="not-invited">Not invited</option>}
    </select>
    {showInterest && (
      <select className="tn-input h-9 w-auto" value={interest} onChange={(e) => setInterest(e.target.value)} data-testid={`admin-${name}-interest-filter`}>
        <option value="all">All interests</option>
        {INTERESTS.map((i) => <option key={i} value={i}>{i}</option>)}
      </select>
    )}
  </div>
);

export const EntryTable = ({ rows, cols, name, kind, headers, onRows }) => {
  const [q, setQ] = useState("");
  const [status, setStatus] = useState("all");
  const [interest, setInterest] = useState("all");
  const [toDelete, setToDelete] = useState(null);
  const [selected, setSelected] = useState([]);
  const isWait = kind === "waitlist";

  const shown = useMemo(() => rows.filter((r) =>
    (status === "all" ||
      (status === "invited" ? !!r.invited_at : status === "not-invited" ? !r.invited_at : (status === "contacted") === !!r.contacted)) &&
    (interest === "all" || r.interest === interest) &&
    (!q || cols.some(([k]) => String(r[k] ?? "").toLowerCase().includes(q.toLowerCase())))
  ), [rows, cols, q, status, interest]);

  const saveNote = async (r, note) => {
    try {
      await api.patch(`/admin/${kind}/${r.id}`, { note }, { headers });
      onRows(rows.map((x) => (x.id === r.id ? { ...x, note } : x)));
      toast.success("Note saved");
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const pick = (id) => setSelected((s) => (s.includes(id) ? s.filter((x) => x !== id) : [...s, id]));
  const allPicked = shown.length > 0 && shown.every((r) => selected.includes(r.id));
  const pickAll = () => setSelected(allPicked ? [] : shown.map((r) => r.id));
  const invited = (at) => { onRows(rows.map((x) => (selected.includes(x.id) ? { ...x, invited_at: at } : x))); setSelected([]); };

  const toggle = async (r) => {
    try {
      await api.patch(`/admin/${kind}/${r.id}`, { contacted: !r.contacted }, { headers });
      onRows(rows.map((x) => (x.id === r.id ? { ...x, contacted: !r.contacted } : x)));
    } catch (e) { toast.error(errorMessage(e)); }
  };

  const remove = async () => {
    try {
      await api.delete(`/admin/${kind}/${toDelete.id}`, { headers });
      onRows(rows.filter((x) => x.id !== toDelete.id));
      setSelected((s) => s.filter((x) => x !== toDelete.id));
      toast.success("Entry deleted");
    } catch (e) { toast.error(errorMessage(e)); }
    setToDelete(null);
  };

  return (
    <div>
      <div className="flex flex-wrap items-center gap-3 mb-4">
        <Filters {...{ name, q, setQ, status, setStatus, interest, setInterest }} showInterest={kind === "waitlist"} />
        <p className="text-sm text-[var(--tn-muted)]">Showing <span className="text-[var(--tn-text)] font-mono" data-testid={`admin-${name}-shown-count`}>{shown.length}</span> of <span className="text-[var(--tn-text)] font-mono" data-testid={`admin-${name}-count`}>{rows.length}</span></p>
        <button className="tn-btn-ghost h-9" onClick={() => exportCsv(shown, cols, name)} disabled={!shown.length} data-testid={`admin-${name}-export-btn`}>
          <Download size={15} /> Export CSV
        </button>
        {isWait && <InviteBar selected={selected} rows={rows} headers={headers} onDone={invited} />}
      </div>
      <div className="rounded-xl border border-[var(--tn-line)] overflow-x-auto">
        <table className="w-full min-w-[1000px] text-sm" data-testid={`admin-${name}-table`}>
          <thead className="bg-[var(--tn-bg-2)]">
            <tr>
              {isWait && <th className="px-4 py-3"><input type="checkbox" checked={allPicked} onChange={pickAll} className="w-4 h-4 accent-[#22d3ee] cursor-pointer" data-testid="admin-waitlist-select-all" /></th>}
              {[...cols.map((c) => c[1]), ...(isWait ? ["Invite"] : []), "Note", "Contacted", ""].map((h, i) => <th key={i} className="text-left font-mono text-[10px] uppercase tracking-wider text-[var(--tn-muted)] font-normal px-4 py-3">{h}</th>)}
            </tr>
          </thead>
          <tbody>
            {shown.map((r) => (
              <tr key={r.id} className={`border-t border-[var(--tn-line)] ${r.contacted ? "opacity-60" : ""}`} data-testid={`admin-${name}-row-${r.id}`}>
                {isWait && <td className="px-4 py-3"><input type="checkbox" checked={selected.includes(r.id)} onChange={() => pick(r.id)} className="w-4 h-4 accent-[#22d3ee] cursor-pointer" data-testid={`admin-waitlist-select-${r.id}`} /></td>}
                {cols.map(([k]) => <td key={k} className={`px-4 py-3 ${k === "position" || k === "referrals" ? "font-mono" : ""}`}>{k === "created_at" ? fmt(r[k]) : r[k]}</td>)}
                {isWait && (
                  <td className="px-4 py-3" data-testid={`admin-waitlist-invited-${r.id}`}>
                    {r.invited_at
                      ? <span className="font-mono text-[10px] uppercase px-2 py-1 rounded border text-[var(--tn-green)] border-[rgba(163,230,53,0.35)] whitespace-nowrap" title={fmt(r.invited_at)}>Invited</span>
                      : <span className="text-xs text-[var(--tn-muted)]">—</span>}
                  </td>
                )}
                <td className="px-4 py-2"><NoteCell key={r.note} row={r} name={name} onSave={saveNote} /></td>
                <td className="px-4 py-3">
                  <input type="checkbox" checked={!!r.contacted} onChange={() => toggle(r)} className="w-4 h-4 accent-[#22d3ee] cursor-pointer" data-testid={`admin-${name}-contacted-${r.id}`} />
                </td>
                <td className="px-4 py-3 text-right">
                  <button onClick={() => setToDelete(r)} className="text-[var(--tn-muted)] hover:text-[var(--tn-red)] transition-colors" aria-label="Delete" data-testid={`admin-${name}-delete-${r.id}`}><Trash2 size={16} /></button>
                </td>
              </tr>
            ))}
            {!shown.length && <tr><td colSpan={cols.length + (isWait ? 5 : 3)} className="px-4 py-10 text-center text-[var(--tn-muted)]">No entries found</td></tr>}
          </tbody>
        </table>
      </div>
      <AlertDialog open={!!toDelete} onOpenChange={(v) => !v && setToDelete(null)}>
        <AlertDialogContent className="bg-[var(--tn-card)] border-[var(--tn-line)] text-[var(--tn-text)]" data-testid="admin-delete-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Delete this entry?</AlertDialogTitle>
            <AlertDialogDescription className="text-[var(--tn-muted)]">{toDelete?.email} will be permanently removed.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-transparent border-[var(--tn-line)]" data-testid="admin-delete-cancel">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={remove} className="bg-[var(--tn-red)] text-black hover:bg-red-300" data-testid="admin-delete-confirm">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};
